import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import * as LucideIcons from 'lucide-react';
import { FieldRender } from './PublicForm';
import {
  CheckCircle2, ArrowRight, ShoppingCart, ExternalLink, Loader2, Shield,
  Star, Sparkles, PlayCircle, Quote, Clock, ChevronDown, Tag, Check, Circle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({
  baseURL: `${API_BASE}/api/public`,
  headers: { 'Content-Type': 'application/json' },
});

export const parseFunnelPath = (pathname) => {
  const m = pathname.match(/^\/funnel\/([a-z0-9-]+)(?:\/(\d+))?\/?$/i);
  if (!m) return null;
  return { slug: m[1], step: m[2] != null ? parseInt(m[2], 10) : 0 };
};

// ── Video helpers ────────────────────────────────────────────────────────────
const parseVideo = (url) => {
  if (!url) return null;
  const yt1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/i);
  if (yt1) return { type: 'youtube', embed: `https://www.youtube.com/embed/${yt1[1]}?rel=0` };
  const vimeo = url.match(/vimeo\.com\/(\d+)/i);
  if (vimeo) return { type: 'vimeo', embed: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return { type: 'file', src: url };
  return { type: 'unknown', src: url };
};

// ── Public funnel — ClickFunnels-style multi-section render ──────────────────
const PublicFunnel = ({ slug, initialStep = 0 }) => {
  const [stepData, setStepData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [stepIdx, setStepIdx] = useState(initialStep);

  const load = useCallback(async (idx) => {
    setLoading(true); setErr(''); setValues({}); setShowThanks(false);
    try {
      const { data } = await publicApi.get(`/funnels/${slug}/${idx}`);
      setStepData(data);
      setStepIdx(idx);
      const url = `/funnel/${slug}${idx > 0 ? `/${idx}` : ''}`;
      if (window.location.pathname !== url) window.history.pushState({}, '', url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Funnel not available');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(initialStep); }, [load, initialStep]);

  useEffect(() => {
    const onPop = () => {
      const p = parseFunnelPath(window.location.pathname);
      if (p && p.slug === slug) load(p.step);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [load, slug]);

  // Unlock body scroll while on public funnel
  useEffect(() => {
    document.body.classList.add('pf-body');
    return () => document.body.classList.remove('pf-body');
  }, []);

  const theme = stepData?.theme || {};

  const themeStyle = useMemo(() => ({
    '--pf-primary': theme.primary_color || '#3368b8',
    '--pf-secondary': theme.secondary_color || '#26456f',
    '--pf-bg': theme.background_color || '#ffffff',
    '--pf-text': theme.text_color || '#0f1923',
    '--pf-accent': theme.accent_color || '#ff7a00',
    '--pf-font': theme.font_family || 'Inter, system-ui, sans-serif',
    fontFamily: theme.font_family || 'Inter, system-ui, sans-serif',
    background: theme.background_color || '#ffffff',
    color: theme.text_color || '#0f1923',
  }), [theme]);

  useEffect(() => {
    if (theme.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = theme.favicon_url;
    }
    if (stepData?.name) document.title = stepData.name;
  }, [theme.favicon_url, stepData?.name]);

  if (loading) return <div className="pf-shell" style={themeStyle}><div className="pf-loading"><Loader2 size={24} className="spin" /> Loading…</div></div>;
  if (err) return <div className="pf-shell" style={themeStyle}><div className="pf-error">{err}</div></div>;
  if (!stepData) return null;

  const step = stepData.step;
  const nextIdx = stepData.next_step_index;
  const isOptin = step.type === 'optin';
  const isSalesOrUpsell = step.type === 'sales' || step.type === 'upsell';
  const isThankyou = step.type === 'thankyou';
  const isCustom = step.type === 'custom_html';

  const goNext = () => { if (nextIdx != null) load(nextIdx); else setShowThanks(true); };

  const handleCTA = async () => {
    if (step.cta_action === 'external_url' && step.cta_url) { window.location.href = step.cta_url; return; }
    if (step.cta_action === 'checkout' && step.product) { await startCheckout(step.product, values.email); return; }
    goNext();
  };

  const startCheckout = async (product, customerEmail) => {
    setBusy(true); setErr('');
    try {
      const { data } = await publicApi.post('/commerce/checkout', {
        product_id: product.id, origin_url: window.location.origin,
        customer_email: customerEmail || null, quantity: 1,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (e) { setErr(e?.response?.data?.detail || 'Checkout failed'); }
    finally { setBusy(false); }
  };

  const submitOptin = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr('');
    try {
      const payload = { ...values };
      (step.optin_fields || []).forEach((f) => { if (f.name && values[f.id] != null) payload[f.name] = values[f.id]; });
      await publicApi.post(`/funnels/${slug}/submit`, { step_id: step.id, data: payload });
      goNext();
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Submission failed'); }
    finally { setBusy(false); }
  };

  const ctaButton = (size = 'lg') => (
    <button
      type="button"
      className={`pf-cta pf-cta-${size}`}
      onClick={handleCTA}
      disabled={busy || (step.cta_action === 'checkout' && !step.product)}
      data-testid="pf-cta"
    >
      {step.cta_action === 'checkout' && <ShoppingCart size={size === 'lg' ? 18 : 14} />}
      {step.cta_action === 'external_url' && <ExternalLink size={size === 'lg' ? 18 : 14} />}
      <span>{step.cta_label || 'Continue'}</span>
      {step.cta_action === 'next_step' && <ArrowRight size={size === 'lg' ? 18 : 14} />}
    </button>
  );

  const video = parseVideo(step.video_url);

  return (
    <div className="pf-shell" style={themeStyle} data-testid={`public-funnel-${slug}`}>
      {/* Sticky top nav */}
      <header className="pf-nav">
        <div className="pf-nav-inner">
          <FunnelLogo logoUrl={theme.logo_url} fallback={theme.site_name || stepData.name} />
          {step.cta_label && !isThankyou && !isOptin && (
            <button type="button" className="pf-cta pf-cta-sm pf-cta-nav" onClick={handleCTA} disabled={busy} data-testid="pf-cta-nav">
              {step.cta_label} <ArrowRight size={12} />
            </button>
          )}
        </div>
      </header>

      {/* Journey stepper — shows all funnel steps with current highlighted */}
      {stepData.steps_summary?.length > 1 && (
        <JourneyStepper
          steps={stepData.steps_summary}
          current={stepIdx}
        />
      )}

      {/* Offer banner with live countdown — sits below stepper */}
      {step.offer && (step.offer.label || step.offer.expires_at) && (
        <OfferBanner offer={step.offer} />
      )}

      {isCustom ? (
        <div className="pf-section pf-custom-wrap" data-testid={`pf-step-${stepIdx}`}>
          <CustomHtmlEmbed html={step.custom_html || ''} />
        </div>
      ) : (
        <>
          {/* Hero section */}
          <section className={`pf-hero-section pf-layout-${step.hero_layout || 'center'}`} data-testid={`pf-step-${stepIdx}`}>
            <div className="pf-container">
              <div className="pf-hero-grid">
                <div className="pf-hero-text">
                  {step.urgency && (
                    <UrgencyBadge text={step.urgency} endsAt={step.urgency_ends_at} />
                  )}
                  {step.headline && <h1 className="pf-headline" data-testid="pf-headline">{step.headline}</h1>}
                  {step.subheadline && <p className="pf-subhead">{step.subheadline}</p>}

                  {/* Inline opt-in form in hero */}
                  {isOptin && (
                    <form className="pf-optin" onSubmit={submitOptin} data-testid="pf-optin-form">
                      {(step.optin_fields || []).map((f) => (
                        <div className="fp-field" key={f.id}>
                          <FieldRender field={f} value={values[f.id]} onChange={(v) => setValues((s) => ({ ...s, [f.id]: v }))} />
                        </div>
                      ))}
                      {err && <div className="pf-err">{err}</div>}
                      <button type="submit" className="pf-cta pf-cta-lg" disabled={busy} data-testid="pf-optin-submit">
                        {busy ? 'Submitting…' : (step.cta_label || 'Get Instant Access')} <ArrowRight size={18} />
                      </button>
                      <div className="pf-privacy">🔒 We respect your privacy. Unsubscribe anytime.</div>
                    </form>
                  )}

                  {!isOptin && !isThankyou && step.cta_label && (
                    <div className="pf-hero-cta">
                      {ctaButton('lg')}
                      {step.type === 'upsell' && (
                        <button type="button" className="pf-skip" onClick={goNext} data-testid="pf-skip">
                          No thanks — continue
                        </button>
                      )}
                    </div>
                  )}

                  {isThankyou && (
                    <div className="pf-thankyou" data-testid="pf-thankyou">
                      <CheckCircle2 size={56} />
                      {step.success_message && <p className="pf-thanks-msg">{step.success_message}</p>}
                    </div>
                  )}
                </div>

                {/* Hero media (video OR image) */}
                {(video || step.hero_image_url) && (
                  <div className="pf-hero-media">
                    {video ? (
                      <VideoEmbed video={video} testid="pf-video" />
                    ) : (
                      <img src={step.hero_image_url} alt="" className="pf-hero-img" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Trusted-by logo strip */}
          {step.trusted_by?.length > 0 && (
            <section className="pf-trusted-section" data-testid="pf-trusted-by">
              <div className="pf-container">
                <div className="pf-trusted-label">Trusted by teams at</div>
                <div className="pf-trusted-row">
                  {step.trusted_by.map((t, i) => (
                    <div className="pf-trusted-item" key={i} data-testid={`pf-trusted-${i}`}>
                      {t.logo_url ? (
                        <img src={t.logo_url} alt={t.name} onError={(e) => {
                          e.currentTarget.outerHTML = `<span class="pf-trusted-text">${t.name}</span>`;
                        }} />
                      ) : (
                        <span className="pf-trusted-text">{t.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* How it works */}
          {step.how_it_works?.length > 0 && (
            <section className="pf-section pf-how-section" data-testid="pf-how-it-works">
              <div className="pf-container">
                <h2 className="pf-section-title">How it works</h2>
                <div className="pf-how-grid">
                  {step.how_it_works.map((h, i) => {
                    const Icon = (h.icon && LucideIcons[h.icon]) || Sparkles;
                    return (
                      <div className="pf-how-step" key={i} data-testid={`pf-how-${i}`}>
                        <div className="pf-how-number">{String(i + 1).padStart(2, '0')}</div>
                        <div className="pf-how-icon"><Icon size={22} /></div>
                        <h3 className="pf-how-title">{h.title}</h3>
                        {h.description && <p className="pf-how-desc">{h.description}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Features grid */}
          {step.features?.length > 0 && (
            <section className="pf-section pf-features-section" data-testid="pf-features">
              <div className="pf-container">
                <h2 className="pf-section-title">What you'll get</h2>
                <div className="pf-features-grid">
                  {step.features.map((f, i) => {
                    const Icon = (f.icon && LucideIcons[f.icon]) || Sparkles;
                    return (
                      <div className="pf-feature" key={i} data-testid={`pf-feature-${i}`}>
                        <div className="pf-feature-icon"><Icon size={22} /></div>
                        <h3 className="pf-feature-title">{f.title}</h3>
                        {f.description && <p className="pf-feature-desc">{f.description}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Body markdown */}
          {step.body_md && (
            <section className="pf-section pf-body-section">
              <div className="pf-container narrow">
                <div className="pf-body"><ReactMarkdown>{step.body_md}</ReactMarkdown></div>
                {step.bullets?.length > 0 && (
                  <ul className="pf-bullets">
                    {step.bullets.map((b, i) => (
                      <li key={i}><CheckCircle2 size={14} /> <span>{b}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {/* Sales product card */}
          {isSalesOrUpsell && step.product && (
            <section className="pf-section pf-product-section" data-testid="pf-product">
              <div className="pf-container">
                <div className="pf-product-card">
                  {step.product.image_url && <img src={step.product.image_url} alt="" className="pf-product-img" />}
                  <div className="pf-product-info">
                    <div className="pf-product-name">{step.product.name}</div>
                    {step.product.description && <div className="pf-product-desc">{step.product.description}</div>}
                    <div className="pf-product-price">
                      ${step.product.price}
                      <span className="pf-product-int">{step.product.interval !== 'one_time' ? ` / ${step.product.interval}` : ''}</span>
                    </div>
                    {ctaButton('lg')}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Testimonials */}
          {step.testimonials?.length > 0 && (
            <section className="pf-section pf-testimonials-section" data-testid="pf-testimonials">
              <div className="pf-container">
                <h2 className="pf-section-title">What people are saying</h2>
                <div className="pf-testimonials-grid">
                  {step.testimonials.map((t, i) => (
                    <figure className="pf-testimonial" key={i} data-testid={`pf-testimonial-${i}`}>
                      <Quote size={18} className="pf-test-quote-mark" />
                      <div className="pf-rating">
                        {Array.from({ length: 5 }).map((_, k) => (
                          <Star key={k} size={14} fill={k < (t.rating || 5) ? 'currentColor' : 'none'} />
                        ))}
                      </div>
                      <blockquote className="pf-test-quote">{t.quote}</blockquote>
                      <figcaption className="pf-test-author">
                        {t.avatar_url && <img src={t.avatar_url} alt="" className="pf-test-avatar" />}
                        <div>
                          <div className="pf-test-name">{t.name}</div>
                          {t.role && <div className="pf-test-role">{t.role}</div>}
                        </div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQ */}
          {step.faq?.length > 0 && (
            <section className="pf-section pf-faq-section" data-testid="pf-faq">
              <div className="pf-container narrow">
                <h2 className="pf-section-title">Frequently asked questions</h2>
                <FaqList items={step.faq} />
              </div>
            </section>
          )}

          {/* Universal 3rd-party embed (Trulli, Calendly, Typeform, custom widgets…).
              Renders on EVERY non-custom step type — scripts execute via CustomHtmlEmbed. */}
          {step.custom_html && step.custom_html.trim() && (
            <section className="pf-section pf-embed-section" data-testid="pf-embed-section">
              <div className="pf-container narrow">
                <CustomHtmlEmbed html={step.custom_html} />
              </div>
            </section>
          )}

          {/* Big final CTA + guarantee */}
          {!isOptin && !isThankyou && step.cta_label && (step.features?.length || step.testimonials?.length || step.body_md) && (
            <section className="pf-section pf-final-cta">
              <div className="pf-container narrow" style={{ textAlign: 'center' }}>
                <h2 className="pf-section-title">Ready when you are</h2>
                {ctaButton('lg')}
                {step.guarantee && (
                  <div className="pf-guarantee" data-testid="pf-guarantee">
                    <Shield size={16} /> {step.guarantee}
                  </div>
                )}
                {err && <div className="pf-err">{err}</div>}
              </div>
            </section>
          )}

          {showThanks && (
            <section className="pf-section pf-thank-final" data-testid="pf-final-thanks">
              <div className="pf-container narrow" style={{ textAlign: 'center' }}>
                <CheckCircle2 size={56} />
                <h2 className="pf-section-title">You're all set</h2>
              </div>
            </section>
          )}
        </>
      )}

      <footer className="pf-foot">Powered by LegacyCRM</footer>

      {/* Floating "recent buyers" notifications — cycles every 6s */}
      {step.recent_buyers?.length > 0 && <RecentBuyersToaster buyers={step.recent_buyers} />}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Progress = ({ count, current }) => (
  <div className="pf-progress" data-testid="pf-progress">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className={`pf-dot${i === current ? ' active' : ''}${i < current ? ' done' : ''}`} />
    ))}
  </div>
);

// Logo that gracefully falls back to text when the image URL is missing OR
// when the URL is not actually an image (e.g. the user pasted a website URL
// like https://acme.com/ instead of an image). On image-load failure we swap
// to the text fallback rather than vanishing the nav-left entirely.
const FunnelLogo = ({ logoUrl, fallback }) => {
  const [broken, setBroken] = useState(false);
  // Heuristic: a URL that ends with "/" (e.g. "https://acme.com/") is clearly
  // a website root and not an image. Skip the broken-img flash and render text.
  const looksLikeWebsiteRoot = logoUrl && /^https?:\/\/[^/]+\/?$/.test(logoUrl);
  if (!logoUrl || broken || looksLikeWebsiteRoot) {
    return <div className="pf-logo-text" data-testid="pf-logo-text">{fallback}</div>;
  }
  return (
    <img
      src={logoUrl}
      alt={fallback || ''}
      className="pf-logo"
      onError={() => setBroken(true)}
      data-testid="pf-logo-img"
    />
  );
};

// Renders user-pasted HTML AND actually executes any embedded <script> tags.
// React's dangerouslySetInnerHTML does not execute scripts (security), so we
// extract scripts and re-create them via document.createElement('script').
//
// Also auto-fixes the common Trulli embed pitfall — for "inline" layouts, the
// Trulli script expects a container whose id matches `trulli-{data-type}`
// (e.g. `trulli-marketplace`). If the pasted HTML uses a different id like
// `trulli-embed`, we silently rename it so the embed actually mounts.
const autofixTrulliContainers = (rootFragment) => {
  const scripts = Array.from(rootFragment.querySelectorAll('script[src*="trulli"]'));
  scripts.forEach((s) => {
    const layout = (s.getAttribute('data-layout') || '').toLowerCase();
    const type = (s.getAttribute('data-type') || '').toLowerCase();
    if (!type) return;
    if (layout && layout !== 'inline') return;  // only the inline layout needs the matching container
    const expectedId = `trulli-${type}`;
    // Already has the right container? Great, nothing to do.
    if (rootFragment.querySelector(`#${CSS.escape(expectedId)}`)) return;
    // Find any other div whose id starts with "trulli-" and rename it.
    const candidate = rootFragment.querySelector('[id^="trulli-"], [id*="trulli"]');
    if (candidate) {
      candidate.id = expectedId;
      return;
    }
    // No container at all — inject one immediately before the script.
    const div = document.createElement('div');
    div.id = expectedId;
    s.parentNode.insertBefore(div, s);
  });
};

const CustomHtmlEmbed = ({ html }) => {
  const ref = React.useRef(null);
  const mountedHtmlRef = React.useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    // Idempotency guard — protects against:
    //   • React StrictMode dev-mode double-invocation (cleanup is intentionally
    //     not returned, so the second invocation sees the same html in the ref
    //     and skips the re-mount entirely).
    //   • Identical re-renders that pass the same html string.
    // When the html prop actually CHANGES, we still wipe the old content and
    // re-mount because mountedHtmlRef.current !== html below.
    if (mountedHtmlRef.current === html) return undefined;
    mountedHtmlRef.current = html;

    // Wipe any previous content (handles html prop changes within same instance)
    root.innerHTML = '';
    // Use a template to parse the HTML string (preserves all attrs)
    const tpl = document.createElement('template');
    tpl.innerHTML = html || '';
    const cloned = document.importNode(tpl.content, true);

    // Auto-fix Trulli inline embeds before we hand the fragment to the DOM.
    autofixTrulliContainers(cloned);

    // Walk and clone <script> nodes so the browser actually runs them
    const scripts = Array.from(cloned.querySelectorAll('script'));
    scripts.forEach((oldScript) => {
      const s = document.createElement('script');
      for (const attr of oldScript.attributes) {
        s.setAttribute(attr.name, attr.value);
      }
      if (oldScript.textContent) s.text = oldScript.textContent;
      oldScript.parentNode.replaceChild(s, oldScript);
    });

    root.appendChild(cloned);

    // NOTE: deliberately NO cleanup returned. Real unmount removes the wrapper
    // div entirely (taking injected widgets with it). StrictMode's pseudo-unmount
    // should NOT wipe the content — otherwise the second mount would re-execute
    // every 3rd-party script and inject the widget twice.
    return undefined;
  }, [html]);

  return <div ref={ref} className="pf-custom-html" />;
};

const RecentBuyersToaster = ({ buyers }) => {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!buyers || buyers.length === 0) return undefined;
    let i = 0;
    let hideT;
    const showOne = () => {
      setIdx(i % buyers.length);
      setShow(true);
      hideT = setTimeout(() => setShow(false), 4500);
      i += 1;
    };
    const initial = setTimeout(showOne, 2500);
    const tick = setInterval(showOne, 7500);
    return () => { clearTimeout(initial); clearInterval(tick); clearTimeout(hideT); };
  }, [buyers]);

  if (!buyers || buyers.length === 0) return null;
  const b = buyers[idx];

  return (
    <div className={`pf-buyer-toast${show ? ' show' : ''}`} data-testid="pf-recent-buyer-toast" aria-live="polite">
      <div className="pf-buyer-dot" />
      <div>
        <div className="pf-buyer-line">
          <b>{b.name}</b>{b.location ? ` from ${b.location}` : ''} just purchased
        </div>
        <div className="pf-buyer-time">{b.time_ago || 'just now'}</div>
      </div>
    </div>
  );
};

// Funnel-wide journey stepper — labeled nodes with connector lines + check on done steps
const JourneyStepper = ({ steps, current }) => {
  const stepLabel = (s, i) => s.title || `Step ${i + 1}`;
  return (
    <div className="pf-stepper" data-testid="pf-stepper">
      <ol className="pf-stepper-list">
        {steps.map((s, i) => {
          const isDone = i < current;
          const isCurrent = i === current;
          return (
            <li
              key={i}
              className={`pf-stepper-item${isDone ? ' done' : ''}${isCurrent ? ' current' : ''}`}
              data-testid={`pf-stepper-item-${i}`}
            >
              <div className="pf-stepper-node">
                {isDone ? <Check size={14} /> : <span className="pf-stepper-n">{i + 1}</span>}
              </div>
              <div className="pf-stepper-label">{stepLabel(s, i)}</div>
              {i < steps.length - 1 && <div className="pf-stepper-connector" />}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// Special-offer banner with live countdown + discount badge
const OfferBanner = ({ offer }) => {
  const [remaining, setRemaining] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!offer?.expires_at) { setRemaining(null); return undefined; }
    const target = new Date(offer.expires_at).getTime();
    if (isNaN(target)) return undefined;
    const tick = () => {
      const ms = target - Date.now();
      if (ms <= 0) { setExpired(true); setRemaining({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setExpired(false);
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offer?.expires_at]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className={`pf-offer-banner${expired ? ' expired' : ''}`} data-testid="pf-offer-banner" role="status">
      <div className="pf-offer-inner">
        {expired ? (
          <div className="pf-offer-label" data-testid="pf-offer-expired">
            <Clock size={14} /> {offer.expired_message || 'This offer has ended.'}
          </div>
        ) : (
          <>
            <div className="pf-offer-label" data-testid="pf-offer-label">
              <Tag size={14} />
              <span>{offer.label || 'Special offer'}</span>
              {offer.discount_code && <span className="pf-offer-code">CODE: <b>{offer.discount_code}</b></span>}
            </div>
            {remaining && (
              <div className="pf-offer-countdown" data-testid="pf-offer-countdown">
                <span className="pf-offer-cd-tag">ENDS IN</span>
                {remaining.d > 0 && (<><span className="pf-offer-cd-cell"><b>{pad(remaining.d)}</b><span>d</span></span><span className="pf-offer-cd-sep">:</span></>)}
                <span className="pf-offer-cd-cell"><b>{pad(remaining.h)}</b><span>h</span></span>
                <span className="pf-offer-cd-sep">:</span>
                <span className="pf-offer-cd-cell"><b>{pad(remaining.m)}</b><span>m</span></span>
                <span className="pf-offer-cd-sep">:</span>
                <span className="pf-offer-cd-cell"><b>{pad(remaining.s)}</b><span>s</span></span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const FaqList = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="pf-faq-list">
      {items.map((it, i) => (
        <div className={`pf-faq-item${open === i ? ' open' : ''}`} key={i} data-testid={`pf-faq-${i}`}>
          <button
            type="button"
            className="pf-faq-question"
            onClick={() => setOpen(open === i ? -1 : i)}
            aria-expanded={open === i}
            data-testid={`pf-faq-toggle-${i}`}
          >
            <span>{it.question}</span>
            <ChevronDown size={18} className="pf-faq-chev" />
          </button>
          <div className="pf-faq-answer-wrap">
            <div className="pf-faq-answer">{it.answer}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Urgency text + optional live countdown timer ("Only 100 spots — ends in 02:14:33")
const UrgencyBadge = ({ text, endsAt }) => {
  const [remaining, setRemaining] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!endsAt) { setRemaining(null); setExpired(false); return undefined; }
    const target = new Date(endsAt).getTime();
    if (isNaN(target)) return undefined;
    const tick = () => {
      const ms = target - Date.now();
      if (ms <= 0) { setExpired(true); setRemaining({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setExpired(false);
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className={`pf-urgency${expired ? ' expired' : ''}${endsAt ? ' has-cd' : ''}`} data-testid="pf-urgency">
      <Clock size={11} />
      <span className="pf-urgency-text">{text}</span>
      {remaining && !expired && (
        <span className="pf-urgency-cd" data-testid="pf-urgency-countdown">
          {remaining.d > 0 && <><b>{pad(remaining.d)}</b>d </>}
          <b>{pad(remaining.h)}</b>h <b>{pad(remaining.m)}</b>m <b>{pad(remaining.s)}</b>s
        </span>
      )}
      {expired && <span className="pf-urgency-cd expired" data-testid="pf-urgency-expired">ended</span>}
    </div>
  );
};


const VideoEmbed = ({ video, testid }) => {
  if (!video) return null;
  if (video.type === 'file') {
    return (
      <div className="pf-video-wrap" data-testid={testid}>
        <video controls playsInline src={video.src} className="pf-video" />
      </div>
    );
  }
  if (video.type === 'youtube' || video.type === 'vimeo') {
    return (
      <div className="pf-video-wrap" data-testid={testid}>
        <iframe
          src={video.embed}
          title="Video"
          className="pf-video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <a className="pf-video-fallback" href={video.src} target="_blank" rel="noreferrer" data-testid={testid}>
      <PlayCircle size={18} /> Watch the video
    </a>
  );
};

export default PublicFunnel;
